import React, { useState } from 'react';
import axios from 'axios';
import { Search, Trash2, Info, AlertTriangle, CheckCircle, XCircle, Loader2, Cloud } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';

function AzureDeviceManager({
  devices = [],
  searchSerial,
  setSearchSerial,
  searchResult,
  setSearchResult,
  batchResults,
  setBatchResults
}) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [batchLoading, setBatchLoading] = useState(false);
  const [batchProgress, setBatchProgress] = useState({ current: 0, total: 0 });

  const clearMessages = () => {
    setError(null);
    setSuccess(null);
  };

  const handleSearch = async () => {
    if (!searchSerial) {
      setError('Please enter a serial number');
      return;
    }

    clearMessages();
    setLoading(true);

    try {
      const response = await axios.post('/api/azure/device/info', {
        serial: searchSerial
      });
      setSearchResult(response.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Search failed');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (serial) => {
    clearMessages();
    setLoading(true);

    try {
      const response = await axios.post('/api/azure/device/delete', {
        serial,
        purge: true
      });
      
      if (response.data.success) {
        setSuccess(response.data.message);
        setSearchResult(null);
        setDeleteConfirm(null);
      } else {
        setError(response.data.error);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Delete failed');
    } finally {
      setLoading(false);
    }
  };

  const handleBatchSearch = async () => {
    if (devices.length === 0) {
      setError('No devices configured. Add devices in the Devices tab first.');
      return;
    }

    clearMessages();
    setBatchLoading(true);
    setBatchProgress({ current: 0, total: devices.length });
    if (setBatchResults) setBatchResults([]);

    try {
      // Search devices one by one and update UI progressively
      for (let i = 0; i < devices.length; i++) {
        const device = devices[i];
        setBatchProgress({ current: i + 1, total: devices.length });

        try {
          const response = await axios.post('/api/azure/device/info', {
            serial: device.serial
          });

          // Add result immediately to the list
          if (setBatchResults) {
            setBatchResults(prev => [...prev, response.data]);
          }
        } catch (err) {
          // Add error result for this device
          if (setBatchResults) {
            setBatchResults(prev => [...prev, {
              serial_number: device.serial,
              status: 'error',
              error: err.response?.data?.error || err.message
            }]);
          }
        }
      }
    } catch (err) {
      console.error('Batch search error:', err);
      const errorMsg = err.response?.data?.error || err.message || 'Batch search failed';
      setError(errorMsg);
    } finally {
      setBatchLoading(false);
      setBatchProgress({ current: 0, total: 0 });
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'found':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'not_found':
        return <XCircle className="w-5 h-5 text-red-500" />;
      case 'error':
        return <AlertTriangle className="w-5 h-5 text-yellow-500" />;
      default:
        return <Info className="w-5 h-5 text-gray-500" />;
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      found: 'bg-green-100 text-green-800',
      not_found: 'bg-red-100 text-red-800',
      error: 'bg-yellow-100 text-yellow-800'
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status] || 'bg-gray-100 text-gray-800'}`}>
        {status === 'found' ? 'Registered' : status === 'not_found' ? 'Not Found' : 'Error'}
      </span>
    );
  };

  const getSourceBadge = (source) => {
    if (!source) return null;
    const styles = source === 'nexus' ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800';
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles}`}>
        {source === 'nexus' ? 'Nexus' : 'Azure IoT Hub'}
      </span>
    );
  };

  return (
    <div className="space-y-6">
      {/* Search Single Device */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Search className="w-5 h-5" />
            Search Device
          </CardTitle>
          <CardDescription>
            Search for a device by serial number to check if it's registered in Nexus or Azure IoT Hub
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="space-y-2 md:col-span-3">
              <Label htmlFor="searchSerial">Serial Number</Label>
              <Input
                id="searchSerial"
                placeholder="00D2000F93"
                value={searchSerial}
                onChange={(e) => setSearchSerial(e.target.value.toUpperCase())}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              />
            </div>
            <div className="flex items-end">
              <Button
                onClick={handleSearch}
                disabled={loading}
                className="w-full flex items-center gap-2"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                Search
              </Button>
            </div>
          </div>

          {/* Search Result */}
          {searchResult && (
            <div className="mt-6 p-4 border rounded-lg bg-gray-50">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  {getStatusIcon(searchResult.status)}
                  <div>
                    <h3 className="font-semibold">{searchResult.serial_number}</h3>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {getSourceBadge(searchResult.source)}
                  {getStatusBadge(searchResult.status)}
                </div>
              </div>

              {searchResult.status === 'found' && (
                <>
                  <div className="grid grid-cols-2 gap-4 mb-4 text-sm">
                    <div>
                      <span className="text-muted-foreground">{searchResult.source === 'nexus' ? 'DSN:' : 'Client ID:'}</span>
                      <p className="font-mono text-xs break-all">{searchResult.client_id}</p>
                    </div>
                    {searchResult.details && (
                      <>
                        <div>
                          <span className="text-muted-foreground">Device Name:</span>
                          <p>{searchResult.details.name || 'N/A'}</p>
                        </div>
                      </>
                    )}
                  </div>

                  {/* Delete Button — Nexus device deletion is not supported here */}
                  {searchResult.source === 'nexus' ? (
                    <p className="text-sm text-muted-foreground">
                      This device is registered in Nexus. Delete is only available for devices in Azure IoT Hub.
                    </p>
                  ) : deleteConfirm === searchResult.serial_number ? (
                    <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                      <AlertTriangle className="w-5 h-5 text-red-500" />
                      <span className="text-sm text-red-700 flex-1">
                        Are you sure you want to delete this device? This action cannot be undone.
                      </span>
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => handleDelete(searchResult.serial_number)}
                        disabled={loading}
                      >
                        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Confirm Delete'}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setDeleteConfirm(null)}
                      >
                        Cancel
                      </Button>
                    </div>
                  ) : (
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => setDeleteConfirm(searchResult.serial_number)}
                      className="flex items-center gap-2"
                    >
                      <Trash2 className="w-4 h-4" />
                      Delete from Azure
                    </Button>
                  )}
                </>
              )}

              {searchResult.status === 'not_found' && (
                <p className="text-sm text-muted-foreground">
                  This device is not registered in Nexus or Azure IoT Hub.
                </p>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Batch Search - Check All Configured Devices */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Cloud className="w-5 h-5" />
            Check All Configured Devices
          </CardTitle>
          <CardDescription>
            Verify which of your configured devices are registered in Nexus or Azure IoT Hub
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-4 mb-4">
            <Button
              onClick={handleBatchSearch}
              disabled={batchLoading || devices.length === 0}
              className="flex items-center gap-2"
            >
              {batchLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
              Check {devices.length} Device{devices.length !== 1 ? 's' : ''}
            </Button>
            {batchLoading && batchProgress.total > 0 && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <span>Checking device {batchProgress.current} of {batchProgress.total}...</span>
                <div className="w-32 h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-blue-500 transition-all duration-300"
                    style={{ width: `${(batchProgress.current / batchProgress.total) * 100}%` }}
                  />
                </div>
              </div>
            )}
          </div>

          {batchResults && batchResults.length > 0 && (
            <div className="border rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="px-4 py-2 text-left">Serial Number</th>
                    <th className="px-4 py-2 text-left">Source</th>
                    <th className="px-4 py-2 text-left">Status</th>
                    <th className="px-4 py-2 text-left">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {batchResults.map((result, idx) => (
                    <tr key={idx} className="border-t hover:bg-gray-50">
                      <td className="px-4 py-2 font-mono">{result.serial_number}</td>
                      <td className="px-4 py-2">{getSourceBadge(result.source)}</td>
                      <td className="px-4 py-2">
                        <div className="flex items-center gap-2">
                          {getStatusIcon(result.status)}
                          {getStatusBadge(result.status)}
                        </div>
                      </td>
                      <td className="px-4 py-2">
                        {result.status === 'found' && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setSearchSerial(result.serial_number);
                              setSearchResult(result);
                            }}
                            className="text-xs"
                          >
                            <Info className="w-3 h-3 mr-1" />
                            Details
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              
              {/* Summary */}
              <div className="px-4 py-3 bg-gray-50 border-t text-sm">
                <span className="text-green-600 font-medium">
                  {batchResults.filter(r => r.status === 'found').length} registered
                </span>
                {' · '}
                <span className="text-red-600 font-medium">
                  {batchResults.filter(r => r.status === 'not_found').length} not found
                </span>
                {batchResults.some(r => r.status === 'error') && (
                  <>
                    {' · '}
                    <span className="text-yellow-600 font-medium">
                      {batchResults.filter(r => r.status === 'error').length} errors
                    </span>
                  </>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Messages */}
      {error && (
        <Card className="border-destructive">
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 text-destructive">
              <XCircle className="w-5 h-5" />
              <p className="font-medium">{error}</p>
            </div>
          </CardContent>
        </Card>
      )}

      {success && (
        <Card className="border-green-500">
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 text-green-600">
              <CheckCircle className="w-5 h-5" />
              <p className="font-medium">{success}</p>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

export default AzureDeviceManager;
